import React, { useContext, useState } from "react"
import PropTypes from "prop-types"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import DownloadIcon from "@mui/icons-material/Download"
import CertHistoryContext from "context/certHistory/CertHistoryContext"
import AuthContext from "context/auth/authContext"
import { Box, Checkbox, Tooltip } from "@mui/material"
import i18n from "i18n"
import { grey } from "@mui/material/colors"
import { getCurrency } from "utils/functions"

function formatRevisionDateLocal(iso) {
  if (iso == null || iso === "") return iso ?? ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

const CertHistoryRowHeader = ({
  title,
  date,
  showDownloadIcon,
  showSelectionCheckbox = false,
  selectionChecked = false,
  selectionIndeterminate = false,
  selectionLabel = "Seleccionar",
  onToggleSelection = () => {},
  totalCertPrice,
  totalInvoicedAmount,
  certId,
  lastCert = false,
}) => {
  const [showIcon, setShowIcon] = useState(false)
  const { setOpen, setSelectedCertificationId } =
    useContext(CertHistoryContext).exportSideBar
  const { setCetToDelete, setOpenConfirmDelete } =
    useContext(CertHistoryContext)

  const { user } = useContext(AuthContext)

  const handleDeleteClick = e => {
    if (lastCert) {
      e.stopPropagation()
      setCetToDelete(certId)
      setOpenConfirmDelete(true)
    }
  }

  const currencySymbol = getCurrency(user["custom:currency"])

  return (
    <Box
      onClick={() => setShowIcon(!showIcon)}
      sx={{
        backgroundColor: "white",
        height: "44px",
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        width: "100%",
        px: 1,
        gap: 1,
        "&:hover": {
          backgroundColor: "#ccd8fc",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "35px",
          flex: "0 0 35px",
        }}
      >
        {showIcon ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 700, minWidth: "140px", whiteSpace: "nowrap" }}
        >
          {title}
        </Box>
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          {formatRevisionDateLocal(date)}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          ml: "auto",
          flexShrink: 0,
        }}
      >
        {showDownloadIcon && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip
                title={lastCert ? "" : i18n.t("certHistory.tooltipMsg")}
                disableHoverListener={lastCert}
              >
                <span>
                  <DeleteOutlineIcon
                    style={{
                      width: "20px",
                      cursor: lastCert ? "pointer" : "not-allowed",
                      color: lastCert ? "inherit" : grey[400],
                    }}
                    onClick={handleDeleteClick}
                  />
                </span>
              </Tooltip>

              <Tooltip
                title={i18n.t("certHistory.downloadCertificationTooltip")}
              >
                <span>
                  <DownloadIcon
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: "pointer",
                    }}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      setOpen(true)
                      setSelectedCertificationId(certId)
                    }}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Box
                id="precioTotal"
                sx={{
                  fontWeight: "700 !important",
                  minWidth: "210px",
                  backgroundColor: "white",
                  color: "black !important",
                  border: "1px solid black !important",
                  fontFamily: "Helvetica",
                  width: "fit-content",
                  px: "5px",
                  height: "30px",
                }}
              >
                Total cert. {totalCertPrice} {currencySymbol}
              </Box>
              {totalInvoicedAmount != null && (
                <Box
                  className="precioTotal"
                  sx={{ fontWeight: "700 !important" }}
                >
                  Total fact. {totalInvoicedAmount.toFixed(2)} {currencySymbol}
                </Box>
              )}
            </Box>
          </>
        )}
        {showSelectionCheckbox && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              width: "40px",
              flex: "0 0 40px",
              pr: 0.25,
            }}
          >
            <Checkbox
              size="small"
              checked={selectionChecked}
              indeterminate={selectionIndeterminate}
              onClick={e => e.stopPropagation()}
              onChange={event => {
                event.stopPropagation()
                onToggleSelection()
              }}
              inputProps={{ "aria-label": selectionLabel }}
              sx={{ p: 0.25 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

CertHistoryRowHeader.propTypes = {
  title: PropTypes.string,
  date: PropTypes.string,
  showDownloadIcon: PropTypes.bool,
  showSelectionCheckbox: PropTypes.bool,
  selectionChecked: PropTypes.bool,
  selectionIndeterminate: PropTypes.bool,
  selectionLabel: PropTypes.string,
  onToggleSelection: PropTypes.func,
  setOpenSideBar: PropTypes.func,
  totalCertPrice: PropTypes.number,
  totalInvoicedAmount: PropTypes.number,
  certId: PropTypes.string,
  lastCert: PropTypes.bool,
}

export default CertHistoryRowHeader
